/**
 * VenueTerminalPage.jsx — waiter/host backend POS terminal for a
 * HustleMeetups partner venue.
 *
 * Route: /venue-terminal/:vendorId
 *
 * Layout:
 *   Left  — table grid (from the floor plan) with cover count per table
 *   Right — active check: line items, send-to-kitchen, close & pay
 *
 * The terminal is intentionally simple for v1 demo: pick a table,
 * add menu items + beverages, send to kitchen, close bill.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import {
  Utensils, Wine, Coffee, Plus, Send, DollarSign, X, Trash2,
  RefreshCw, ChefHat, Grid3x3, ClipboardList, LogOut, User,
} from "lucide-react";
import PlatformHeader from "@/components/PlatformHeader";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useKiosk, kioskAuthHeader } from "@/lib/kioskAuth";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const authHeader = kioskAuthHeader;

const KIND_LABEL = {
  entree:     { label: "Entrees",  Icon: Utensils },
  soup:       { label: "Soups",    Icon: Utensils },
  main:       { label: "Mains",    Icon: Utensils },
  dessert:    { label: "Desserts", Icon: Utensils },
  side:       { label: "Sides",    Icon: Utensils },
  soft_drink: { label: "Soft",     Icon: Coffee },
  beer:       { label: "Beer",     Icon: Wine },
  red_wine:   { label: "Red",      Icon: Wine },
  white_wine: { label: "White",    Icon: Wine },
  bubbles:    { label: "Bubbles",  Icon: Wine },
  spirit:     { label: "Spirits",  Icon: Wine },
  tea_coffee: { label: "T & C",    Icon: Coffee },
};

export default function VenueTerminalPage() {
  const { vendorId } = useParams();
  const { staff, signOut } = useKiosk(vendorId);
  const [menu, setMenu]           = useState({ food: [], beverages: [] });
  const [tickets, setTickets]     = useState([]);
  const [floor, setFloor]         = useState(null);
  const [activeTicket, setActive] = useState(null);
  const [cat, setCat]             = useState("main");
  const [view, setView]           = useState("service"); // service | floor | prep

  const cfg = { headers: authHeader(), withCredentials: true };

  const load = async () => {
    // Each request is independent so a 401 on /tickets does not wipe
    // out the menu and floor plan for the anonymous demo view.
    try {
      const { data } = await axios.get(`${API}/venue-terminal/${vendorId}/menu`, cfg);
      setMenu(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Menu load failed");
    }
    try {
      const { data } = await axios.get(`${API}/venue-terminal/${vendorId}/floor-plan`, cfg);
      setFloor(data.floor_plan || null);
    } catch (e) { /* floor plan optional */ }
    try {
      const { data } = await axios.get(`${API}/venue-terminal/${vendorId}/tickets`, cfg);
      setTickets(data.tickets || []);
    } catch (_) {
      setTickets([]); // Not logged in — that's fine for the demo view.
    }
  };

  useEffect(() => {
    document.title = "Venue Terminal — HustleMeetups";
    load();
  }, [vendorId]);

  const tables = useMemo(() => {
    const els = floor?.elements || [];
    return els.filter((e) => (e.kind || "").startsWith("table"));
  }, [floor]);

  const openTable = async (t) => {
    // Reuse the open ticket for this table if there is one.
    const existing = tickets.find(
      (tk) => tk.table_no === t.label && tk.status === "open");
    if (existing) return setActive(existing);
    try {
      const { data } = await axios.post(
        `${API}/venue-terminal/${vendorId}/tickets`,
        { table_no: t.label, guests: t.seats || 2 }, cfg);
      setActive(data);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Open failed");
    }
  };

  const addItem = async (item, kind) => {
    if (!activeTicket) return;
    try {
      const { data } = await axios.post(
        `${API}/venue-terminal/tickets/${activeTicket.id}/items`,
        {
          sku:      item.id,
          name:     item.name,
          price:    item.price,
          qty:      1,
          kind:     kind === "food" ? "food" : "beverage",
          category: item.category,
        }, cfg);
      setActive(data);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add failed");
    }
  };

  const removeLine = async (line_id) => {
    try {
      const { data } = await axios.delete(
        `${API}/venue-terminal/tickets/${activeTicket.id}/items/${line_id}`, cfg);
      setActive(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remove failed");
    }
  };

  const sendToKitchen = async () => {
    try {
      const { data } = await axios.post(
        `${API}/venue-terminal/tickets/${activeTicket.id}/send-to-kitchen`, {}, cfg);
      toast.success(`${data.lines_sent || 0} line(s) fired to kitchen`);
      setActive(data.ticket);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    }
  };

  const closeBill = async (method) => {
    try {
      const { data } = await axios.post(
        `${API}/venue-terminal/tickets/${activeTicket.id}/close`,
        { payment_method: method,
          amount_paid:    activeTicket?.total || 0 }, cfg);
      toast.success(`Ticket closed — paid via ${method}. Locking kiosk for next PIN.`);
      setActive(null);
      load();
      // Every completed transaction re-locks the kiosk so the next
      // staff member must enter their own PIN.
      signOut();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Close failed");
    }
  };

  const foodByCat  = useMemo(() => {
    const out = {};
    (menu.food || []).forEach((f) => {
      (out[f.category] = out[f.category] || []).push(f);
    });
    return out;
  }, [menu]);
  const bevByCat = useMemo(() => {
    const out = {};
    (menu.beverages || []).forEach((f) => {
      (out[f.category] = out[f.category] || []).push(f);
    });
    return out;
  }, [menu]);

  const catList = ["entree", "soup", "main", "dessert", "side",
                   "soft_drink", "beer", "red_wine", "white_wine", "bubbles"];
  const isFoodCat = (c) => ["entree","soup","main","dessert","side"].includes(c);

  return (
    <div
      data-testid="venue-terminal-page"
      className="min-h-screen bg-slate-50 text-stone-900 dark:bg-[#0a0908] dark:text-stone-100 transition-colors"
    >
      <PlatformHeader
        brand={{ name: `Venue Terminal — ${vendorId}`, to: `/venue-terminal/${vendorId}` }}
        showBookCta={false} showCart={false}
      />

      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-4">
        <header className="flex items-center gap-3 mb-3 flex-wrap">
          <ChefHat size={22} className="text-amber-500"/>
          <h1 className="text-xl md:text-2xl font-black">Backend Terminal</h1>
          {staff && (
            <span
              data-testid="terminal-staff-badge"
              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-emerald-500/15 text-emerald-500 font-black px-2 py-1 rounded-full"
            >
              <User size={11}/> {staff.name} · {staff.role}
            </span>
          )}
          <div className="ml-auto flex items-center gap-2 flex-wrap">
            <div
              data-testid="terminal-tabs"
              className="inline-flex rounded-full bg-stone-100 dark:bg-stone-900 p-1"
            >
              {[
                ["service", ClipboardList, "Service"],
                ["floor",   Grid3x3,       "Floor"],
                ["prep",    ChefHat,       "Kitchen"],
              ].map(([k, Icon, label]) => (
                <button
                  key={k}
                  data-testid={`tab-${k}`}
                  onClick={() => setView(k)}
                  className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-black ${
                    view === k
                      ? "bg-amber-500 text-slate-950"
                      : "text-stone-500"
                  }`}
                >
                  <Icon size={12}/> {label}
                </button>
              ))}
            </div>
            <Button asChild
              className="rounded-full bg-emerald-500 text-slate-950 hover:bg-emerald-400 font-black h-10 text-xs"
              data-testid="btn-open-kitchen"
            >
              <Link to={`/venue-terminal/${vendorId}/kitchen`}>
                <ChefHat size={12} className="mr-1"/> Kitchen View
              </Link>
            </Button>
            <Button onClick={load} variant="outline" className="rounded-full font-black">
              <RefreshCw size={14} className="mr-1"/> Refresh
            </Button>
            <Button
              onClick={signOut}
              variant="outline"
              data-testid="btn-signout"
              className="rounded-full font-black"
              title="Lock kiosk for next staff member"
            >
              <LogOut size={14}/>
            </Button>
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {view === "service" && (
          <>
          {/* Tables */}
          <section
            data-testid="terminal-tables"
            className="lg:col-span-4 rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-4"
          >
            <h2 className="text-sm font-black uppercase tracking-widest text-stone-500 mb-3">Tables</h2>
            <div className="grid grid-cols-3 gap-2">
              {tables.map((t) => {
                const open = tickets.find(
                  (tk) => tk.table_no === t.label && tk.status !== "closed" && tk.status !== "voided");
                const isActive = activeTicket && open && open.id === activeTicket.id;
                return (
                  <button
                    key={t.id}
                    data-testid={`table-btn-${t.label}`}
                    onClick={() => openTable(t)}
                    className={`rounded-xl border p-3 text-left text-sm font-black transition
                      ${isActive
                        ? "border-amber-400 bg-amber-500/20"
                        : open
                          ? "border-emerald-400 bg-emerald-500/10"
                          : "border-stone-200 dark:border-stone-800 hover:bg-stone-50 dark:hover:bg-stone-900"}`}
                  >
                    <div className="text-xl">{t.label}</div>
                    <div className="text-[10px] text-stone-500 mt-1">{t.seats} seats</div>
                    {open && (
                      <div className="text-[10px] text-emerald-600 mt-1">${open.total?.toFixed(2) || "0.00"}</div>
                    )}
                  </button>
                );
              })}
            </div>
          </section>

          {/* Menu + active ticket */}
          <section className="lg:col-span-8 space-y-4">
            <div
              data-testid="terminal-menu"
              className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-4"
            >
              <h2 className="text-sm font-black uppercase tracking-widest text-stone-500 mb-3">Menu</h2>
              <div className="flex gap-2 flex-wrap mb-3">
                {catList.map((c) => (
                  <button
                    key={c}
                    data-testid={`cat-tab-${c}`}
                    onClick={() => setCat(c)}
                    className={`rounded-full px-3 py-1 text-xs font-black ${cat === c
                      ? "bg-amber-500 text-slate-950"
                      : "bg-stone-100 dark:bg-stone-800 text-stone-500"}`}
                  >
                    {KIND_LABEL[c]?.label || c}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {((isFoodCat(cat) ? foodByCat[cat] : bevByCat[cat]) || []).map((it) => (
                  <button
                    key={it.id}
                    data-testid={`menu-item-${it.id}`}
                    disabled={!activeTicket}
                    onClick={() => addItem(it, isFoodCat(cat) ? "food" : "beverage")}
                    className={`rounded-lg border border-stone-200 dark:border-stone-800 p-2 text-left ${!activeTicket ? "opacity-40" : "hover:bg-amber-500/10"}`}
                  >
                    <div className="text-xs font-black">{it.name}</div>
                    <div className="text-[10px] text-stone-500 line-clamp-2 min-h-[24px]">{it.description}</div>
                    <div className="mt-1 flex items-center justify-between">
                      <span className="text-xs font-black text-amber-600">${it.price?.toFixed(2)}</span>
                      <Plus size={12} className="text-stone-400"/>
                    </div>
                  </button>
                ))}
              </div>
              {!activeTicket && (
                <p className="text-xs text-stone-500 mt-3">
                  Open a table to start adding items.
                </p>
              )}
            </div>

            {activeTicket && (
              <div
                data-testid="terminal-active-ticket"
                className="rounded-2xl border border-amber-300 dark:border-amber-800 bg-gradient-to-br from-amber-400/10 to-orange-500/10 p-4"
              >
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-black">Table {activeTicket.table_no}</h2>
                  <span className="text-[10px] uppercase tracking-widest bg-white dark:bg-slate-950 px-2 py-0.5 rounded-full">
                    {activeTicket.status}
                  </span>
                  <div className="ml-auto text-xl font-black">
                    ${activeTicket.total?.toFixed(2) || "0.00"}
                  </div>
                </div>
                <ul className="mt-3 divide-y divide-stone-200 dark:divide-stone-800">
                  {(activeTicket.items || []).map((it) => (
                    <li
                      key={it.line_id}
                      data-testid={`line-${it.line_id}`}
                      className="py-2 flex items-center gap-2"
                    >
                      <span className="text-sm font-bold flex-1">{it.name}</span>
                      <span className="text-xs text-stone-500">
                        {it.sent ? "· fired" : "· new"}
                      </span>
                      <span className="text-sm font-black w-16 text-right">${(it.price * it.qty).toFixed(2)}</span>
                      {!it.sent && (
                        <button
                          onClick={() => removeLine(it.line_id)}
                          data-testid={`btn-remove-${it.line_id}`}
                          className="text-red-500 hover:opacity-70"
                        >
                          <Trash2 size={14}/>
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
                <div className="mt-3 flex gap-2 flex-wrap justify-end">
                  <Button
                    onClick={sendToKitchen}
                    data-testid="btn-send-kitchen"
                    className="rounded-full bg-orange-500 text-white hover:bg-orange-400 font-black"
                  >
                    <Send size={14} className="mr-1"/> Send to kitchen
                  </Button>
                  <Button
                    onClick={() => closeBill("cash")}
                    data-testid="btn-close-cash"
                    className="rounded-full bg-emerald-500 text-slate-950 hover:bg-emerald-400 font-black"
                  >
                    <DollarSign size={14} className="mr-1"/> Cash
                  </Button>
                  <Button
                    onClick={() => closeBill("card")}
                    data-testid="btn-close-card"
                    className="rounded-full bg-sky-500 text-white hover:bg-sky-400 font-black"
                  >
                    <DollarSign size={14} className="mr-1"/> Card
                  </Button>
                  <Button
                    onClick={() => setActive(null)}
                    variant="outline"
                    className="rounded-full font-black"
                  >
                    <X size={14} className="mr-1"/> Close view
                  </Button>
                </div>
              </div>
            )}
          </section>
          </>
          )}

          {view === "floor" && (
            <section
              data-testid="terminal-floor-view"
              className="lg:col-span-12 rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-4"
            >
              <h2 className="text-sm font-black uppercase tracking-widest text-stone-500 mb-3">
                Floor plan — {floor?.name || "Ground floor"}
              </h2>
              {!floor ? (
                <p className="text-sm text-stone-500">Loading floor plan…</p>
              ) : (
                <div className="relative bg-stone-50 dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-xl overflow-hidden"
                     style={{ aspectRatio: `${floor.canvas?.width_m || 18}/${floor.canvas?.height_m || 12}` }}>
                  {(floor.elements || []).map((el) => {
                    const w = ((el.diameter_m || el.length_m || el.w_m || 1) / (floor.canvas?.width_m || 18)) * 100;
                    const h = ((el.diameter_m || el.thickness_m || el.h_m || 1) / (floor.canvas?.height_m || 12)) * 100;
                    const x = (el.x_m / (floor.canvas?.width_m || 18)) * 100;
                    const y = (el.y_m / (floor.canvas?.height_m || 12)) * 100;
                    const isTable = String(el.kind || "").startsWith("table");
                    const open = isTable && tickets.find((tk) => tk.table_no === el.label && tk.status !== "closed" && tk.status !== "voided");
                    if (!isTable) {
                      return (
                        <div key={el.id}
                             className="absolute bg-stone-300 dark:bg-stone-700 flex items-center justify-center text-[10px] font-black text-stone-600 dark:text-stone-300 rounded-sm"
                             style={{ left: `${x}%`, top: `${y}%`, width: `${w}%`, height: `${Math.max(1.2, h)}%` }}>
                          {el.label || el.kind}
                        </div>
                      );
                    }
                    return (
                      <button
                        key={el.id}
                        data-testid={`floor-table-${el.label}`}
                        onClick={() => { setView("service"); openTable(el); }}
                        className={`absolute rounded-full flex items-center justify-center font-black text-slate-950 border-2 ${
                          open ? "bg-emerald-400 border-emerald-600" : "bg-amber-300 border-amber-500"}`}
                        style={{ left: `${x}%`, top: `${y}%`, width: `${w * 1.2}%`, aspectRatio: "1/1" }}
                      >
                        <div className="text-center leading-tight">
                          <div className="text-sm">{el.label}</div>
                          <div className="text-[9px]">{el.seats}</div>
                          {open && <div className="text-[9px]">${open.total?.toFixed(0)}</div>}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </section>
          )}

          {view === "prep" && (
            <section
              data-testid="terminal-prep-view"
              className="lg:col-span-12 rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-4"
            >
              <h2 className="text-sm font-black uppercase tracking-widest text-stone-500 mb-3">
                Kitchen prep status
              </h2>
              <PrepStatusView vendorId={vendorId}/>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Compact in-terminal preview of the KDS — table# · dish · phase ·   */
/* countdown. Uses the /kds endpoint the fullscreen KDS uses.         */
/* ------------------------------------------------------------------ */
function PrepStatusView({ vendorId }) {
  const [rows, setRows] = useState([]);
  const cfg = { headers: kioskAuthHeader(), withCredentials: true };
  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/venue-terminal/${vendorId}/kds`, cfg);
      setRows(data.kds || []);
    } catch { /* keep last */ }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorId]);

  const phase = (it) => (it.status || "queued");
  const phaseColour = (p) => ({
    queued:  "bg-stone-500/15 text-stone-500",
    cooking: "bg-amber-500/20 text-amber-600",
    plating: "bg-sky-500/20 text-sky-600",
    ready:   "bg-emerald-500/25 text-emerald-600",
  }[p] || "bg-stone-500/15 text-stone-500");
  const eta = (it) => {
    if (!it.expected_finish_at) return "";
    const ms = new Date(it.expected_finish_at).getTime() - Date.now();
    if (ms <= 0) return "due";
    return `${Math.ceil(ms / 60000)}m`;
  };
  if (rows.length === 0)
    return <p className="text-sm text-stone-500">Nothing fired yet — kitchen is clear.</p>;
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-[10px] uppercase tracking-widest text-stone-500 font-black">
          <th className="py-2">Table</th>
          <th>Dish</th>
          <th>Phase</th>
          <th>ETA</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-stone-200 dark:divide-stone-800">
        {rows.flatMap((r) => (r.items || []).map((it) => (
          <tr key={it.line_id} data-testid={`prep-row-${it.line_id}`}>
            <td className="py-2 font-black text-amber-500">#{r.table_no}</td>
            <td>{it.qty}× {it.name}</td>
            <td>
              <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest ${phaseColour(phase(it))}`}>
                {phase(it)}
              </span>
            </td>
            <td className="tabular-nums text-stone-500">{eta(it)}</td>
          </tr>
        )))}
      </tbody>
    </table>
  );
}